import HomeFilledIcon from '@mui/icons-material/HomeFilled';
import PersonalInjuryIcon from '@mui/icons-material/PersonalInjury';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import GroupIcon from '@mui/icons-material/Group';
import BadgeIcon from '@mui/icons-material/Badge';
import { Navigation } from '@toolpad/core';
import { SystemRole } from '../helper-types/general/enums/system-role';
import { SystemClaim } from '../helper-types/general/enums/system-claim';
import { useTranslation } from 'react-i18next';
import { Person, SubscriptionsOutlined } from '@mui/icons-material';

interface NavItem {
  kind?: 'header' | 'divider';
  segment?: string;
  title?: string;
  icon?: React.ReactNode;
  children?: NavItem[];
  requiredRoles?: string[];
}

const getNavigation = (userRoles: string[], isAuthenticated: boolean): Navigation => {
  const { t } = useTranslation();
  const navItems: NavItem[] = [
    {
      kind: 'header',
      title: t('nav.mainMenu'),
      requiredRoles: [
        SystemRole.Administrator.toString(),
        SystemRole.Physician.toString(),
        SystemClaim.AccessPatientsGrid.toString(),
        SystemClaim.AccessAppointmentsGrid.toString(),
        SystemClaim.AccessPrescriptionsGrid.toString(),
        SystemClaim.AccessUserManagementMenu.toString(),
      ],
    },
    {
      segment: '',
      title: t('nav.home'),
      icon: <HomeFilledIcon />,
      requiredRoles: [
        SystemRole.Administrator.toString(),
        SystemRole.Physician.toString(),
        SystemClaim.AccessPatientsGrid.toString(),
        SystemClaim.AccessAppointmentsGrid.toString(),
        SystemClaim.AccessPrescriptionsGrid.toString(),
        SystemClaim.AccessUserManagementMenu.toString(),
      ],
    },
    {
      segment: 'patient-table',
      title: t('nav.patients'),
      icon: <PersonalInjuryIcon />,
      requiredRoles: [SystemRole.Administrator.toString(), SystemRole.Physician.toString(), SystemClaim.AccessPatientsGrid.toString()],
    },
    {
      segment: 'appointment-queue',
      title: t('nav.appointments'),
      icon: <CalendarMonthIcon />,
      requiredRoles: [SystemRole.Administrator.toString(), SystemRole.Physician.toString(), SystemClaim.AccessAppointmentsGrid.toString()],
    },
    {
      segment: 'prescription-table',
      title: t('nav.prescriptions'),
      icon: <AssignmentIcon />,
      requiredRoles: [SystemRole.Administrator.toString(), SystemRole.Physician.toString(), SystemClaim.AccessPrescriptionsGrid.toString()],
    },
    {
      segment: 'security',
      title: t('nav.security'),
      icon: <AdminPanelSettingsIcon />,
      requiredRoles: [
        SystemRole.Administrator.toString(),
        SystemRole.Physician.toString(),
        SystemClaim.AccessPatientsGrid.toString(),
        SystemClaim.AccessAppointmentsGrid.toString(),
        SystemClaim.AccessPrescriptionsGrid.toString(),
        SystemClaim.AccessUserManagementMenu.toString(),
      ],
      children: [
        {
          segment: 'profile',
          title: t('nav.profile'),
          icon: <Person />,
          requiredRoles: [
            SystemRole.Administrator.toString(),
            SystemRole.Physician.toString(),
            SystemClaim.AccessPatientsGrid.toString(),
            SystemClaim.AccessAppointmentsGrid.toString(),
            SystemClaim.AccessPrescriptionsGrid.toString(),
            SystemClaim.AccessUserManagementMenu.toString(),
          ],
        },
        {
          segment: 'user-table',
          title: t('nav.users'),
          icon: <GroupIcon />,
          requiredRoles: [SystemRole.Administrator.toString(), SystemRole.Physician.toString(), SystemClaim.AccessUserManagementMenu.toString()],
        },
        {
          segment: 'role-table',
          title: t('nav.roles'),
          icon: <BadgeIcon />,
          requiredRoles: [SystemRole.Administrator.toString(), SystemRole.Physician.toString(), SystemClaim.AccessUserManagementMenu.toString()],
        },
        {
          segment: 'physician-subscriptions',
          title: t('nav.physicianSubscriptions'),
          icon: <SubscriptionsOutlined />,
          requiredRoles: [SystemRole.Administrator.toString()],
        },
      ],
    },
  ];

  // Filter items based on roles and authentication status
  return navItems
    .filter((item) => {
      // Always include the items with no requiredRoles for all users
      if ((!item.requiredRoles || item.requiredRoles.length === 0)) {
        return true;
      }
      // Other items require authentication and matching roles
      return isAuthenticated && item.requiredRoles.some((role) => userRoles.includes(role));
    })
    .map((item) => ({
      ...item,
      // Filter children if present
      children: item.children?.filter((child) =>
        child.requiredRoles?.some((role) => userRoles.includes(role))
      ),
    })) as Navigation;
};

export default getNavigation;